/**
 * A team's add row (redesign §40.8, completing §15): the inline add row with a
 * **native select** (§4, 360 wide, M 32) instead of a field. Its resting label
 * is the prompt without « … » (« Somebody in the organization »), and the
 * secondary M « Add to team » at its right stays at 40 % until somebody is
 * chosen, saying why in the anchored tooltip aligned on its left edge. With
 * nobody left to add the select itself is held at 40 %, aria-disabled, and its
 * tooltip says why. An error sits under the select, in the row; while the add
 * runs the button says « Adding ».
 */
import { useState } from 'react';
import type React from 'react';
import { color, radius } from '../../theme/tokens';
import { useTooltip } from '../../components/Tooltip';
import type { TeamRecord } from '../../lib/types';

export function AddToTeam({
  team,
  candidates,
  onAdd
}: {
  team: string;
  candidates: TeamRecord['candidates'];
  /** Adds them; answers the refusal to show in the row, or null once added. */
  onAdd: (userId: string) => Promise<string | null>;
}): React.JSX.Element {
  const [chosen, setChosen] = useState('');
  const [running, setRunning] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const none = candidates.length === 0;
  const select = useTooltip<HTMLSelectElement>(none ? `Everybody with a seat is already in ${team}` : undefined, 'start');
  const held = none ? `Everybody with a seat is already in ${team}` : !chosen ? 'Choose somebody first' : undefined;
  const button = useTooltip<HTMLButtonElement>(running ? undefined : held, 'start');

  const add = async (): Promise<void> => {
    if (!chosen || running) return;
    setRunning(true);
    setRefusal(null);
    const said = await onAdd(chosen);
    setRunning(false);
    setRefusal(said);
    if (!said) setChosen('');
  };

  return (
    <form
      aria-label={`Add to ${team}`}
      onSubmit={(event) => {
        event.preventDefault();
        void add();
      }}
      style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', padding: '12px 0' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: 360, maxWidth: '100%' }}>
        <select
          value={chosen}
          onChange={(event) => {
            setChosen(event.target.value);
            setRefusal(null);
          }}
          aria-label="Somebody in the organization"
          aria-disabled={none || undefined}
          aria-invalid={refusal ? true : undefined}
          {...select.trigger}
          onMouseDown={none ? (event) => event.preventDefault() : undefined}
          onKeyDown={none ? (event) => event.key !== 'Tab' && event.preventDefault() : undefined}
          style={{
            width: '100%',
            height: 32,
            padding: '0 11px',
            border: `1px solid ${refusal ? color.red : color.lineInput}`,
            borderRadius: 8,
            background: color.surface,
            fontFamily: 'inherit',
            fontSize: 13,
            color: chosen ? color.ink : color.inkSubtle,
            opacity: none ? 0.4 : 1,
            cursor: none ? 'default' : 'pointer'
          }}
        >
          <option value="">Somebody in the organization</option>
          {candidates.map((one) => (
            <option key={one.userId} value={one.userId}>
              {one.name ? `${one.name} — ${one.email}` : one.email}
            </option>
          ))}
        </select>
        {select.tip}
        {refusal && (
          <span role="alert" style={{ fontSize: 12.5, lineHeight: 1.45, color: color.badText }}>
            {refusal}
          </span>
        )}
      </div>
      <button
        type="submit"
        aria-disabled={held || running ? true : undefined}
        {...button.trigger}
        data-press=""
        className="hdr-btn"
        style={{
          flex: 'none',
          height: 32,
          padding: '0 12px',
          border: `1px solid ${color.line}`,
          borderRadius: radius.chip,
          background: 'transparent',
          fontFamily: 'inherit',
          fontSize: 13,
          color: color.inkBody,
          cursor: held || running ? 'default' : 'pointer',
          opacity: held ? 0.4 : 1
        }}
      >
        {running ? 'Adding' : 'Add to team'}
      </button>
      {button.tip}
    </form>
  );
}
